import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Box, CircularProgress, Typography } from "@mui/material";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Backward-compat: old tool share links were generated under /tools/* which is protected.
  // If a shareId is present, redirect to the public /shared/* routes before auth checks.
  // Example old link: /tools/tax-calculator?shareId=...
  const shareId = new URLSearchParams(location.search).get("shareId");
  if (shareId) {
    if (location.pathname === "/tools/tax-calculator") {
      return (
        <Navigate
          to={`/shared/tax-calculator?shareId=${encodeURIComponent(shareId)}`}
          replace
        />
      );
    }
    if (location.pathname === "/tools/housing-forecast") {
      return (
        <Navigate
          to={`/shared/housing-forecast?shareId=${encodeURIComponent(shareId)}`}
          replace
        />
      );
    }
    if (location.pathname === "/tools/mortgage-calculator") {
      return (
        <Navigate
          to={`/shared/mortgage-calculator?shareId=${encodeURIComponent(
            shareId
          )}`}
          replace
        />
      );
    }
    if (
      location.pathname === "/tools/wife-value-calculator" ||
      location.pathname === "/tools/spouse-value-calculator"
    ) {
      return (
        <Navigate
          to={`/shared/spouse-value-calculator?shareId=${encodeURIComponent(
            shareId
          )}`}
          replace
        />
      );
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
          px: 3,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    // Redirect to login page but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
